import React from 'react';
import classNames from 'classnames';

import { PageHeader } from '../shared-components/PageHeader/PageHeader';

import ConsoleUnreachable from './ConsoleUnreachable';
import styles from './LocalAdminConsole.module.scss';
import { findLocalConsoleRoute } from './localConsoleRoutes';
import type { LocalConsoleContext, LocalConsoleRoute } from './types';

export const LOCAL_CONSOLE_TITLE = 'Local Admin Console';
export const LOCAL_CONSOLE_NAV_LABEL = 'Local admin console sections';

export type LocalAdminConsoleViewProps = {
  routes: LocalConsoleRoute[];
  activeRouteId: string;
  onSelectRoute: (routeId: string) => void;
  context: LocalConsoleContext;
  isDeploymentUnreachable?: boolean;
  onRetryDeployment?: () => void;
};

export default function LocalAdminConsoleView({
  routes,
  activeRouteId,
  onSelectRoute,
  context,
  isDeploymentUnreachable = false,
  onRetryDeployment,
}: LocalAdminConsoleViewProps) {
  const activeRoute = findLocalConsoleRoute(activeRouteId, routes);
  const ActiveSection = activeRoute?.Component;

  const renderContent = () => {
    if (isDeploymentUnreachable) {
      return (
        <ConsoleUnreachable
          apiBaseUrl={context.apiBaseUrl}
          organizationName={context.organizationName}
          onRetry={() => onRetryDeployment?.()}
        />
      );
    }
    return ActiveSection ? <ActiveSection {...context} /> : null;
  };

  return (
    <div className={styles.container} data-test-id="localAdminConsole">
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <PageHeader label={LOCAL_CONSOLE_TITLE} />
          <span
            className={classNames(styles.statusPill, {
              [styles.statusPillCloud]: !context.isLocalServer,
            })}
            data-test-id="localAdminConsoleModePill"
          >
            <span
              className={classNames(styles.statusDot, {
                [styles.statusDotCloud]: !context.isLocalServer,
              })}
            />
            {context.isLocalServer ? 'Local server' : 'Cloud'}
          </span>
        </div>
      </header>

      <div className={styles.body}>
        <nav className={styles.nav} aria-label={LOCAL_CONSOLE_NAV_LABEL}>
          <ul className={styles.navList}>
            {routes.map((route) => {
              const selected = route.id === activeRoute?.id;
              return (
                <li key={route.id}>
                  <button
                    type="button"
                    aria-current={selected ? 'page' : undefined}
                    className={classNames(styles.navButton, {
                      [styles.navButtonSelected]: selected,
                    })}
                    data-test-id={`localAdminConsoleNav-${route.id}`}
                    onClick={() => onSelectRoute(route.id)}
                  >
                    {route.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className={styles.content} data-test-id="localAdminConsoleContent">
          {renderContent()}
        </div>
      </div>
    </div>
  );
}
